import React, { useEffect, useState } from "react";
import {
  FaArrowRight,
  FaCheckCircle,
  FaHeart,
  FaShieldAlt,
  FaUsers,
} from "react-icons/fa";

import Reveal from "../../../components/Reveal/Reveal";
import { getSettings } from "../../../services/settingsServices";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3317";

const isExternalURL = (url) => {
  try {
    const newUrl = new URL(url);
    return newUrl.protocol === "http:" || newUrl.protocol === "https:";
  } catch (error) {
    return false;
  }
};

const getImageUrl = (url) => {
  if (!url) return "";
  return isExternalURL(url) ? url : `${BASE_URL}${url}`;
};

const DEFAULT_SETTINGS = {
  nom_club: "MonAppliClub Light",
  logo_club: "",
  about_title: "Un club vivant et accessible",
  about_content:
    "<p>Présentez ici l’histoire du club, ses valeurs, ses disciplines, son fonctionnement et les informations utiles pour accueillir les nouveaux pratiquants.</p>",
};

const strengths = [
  {
    icon: FaUsers,
    title: "Un club humain",
    text: "Une présentation claire pour les adhérents, les familles et les nouveaux visiteurs.",
  },
  {
    icon: FaShieldAlt,
    title: "Une vitrine rassurante",
    text: "Des informations structurées pour valoriser le sérieux et l’organisation du club.",
  },
  {
    icon: FaHeart,
    title: "Une identité forte",
    text: "Logo, contenus, photos et actualités pour faire vivre votre association.",
  },
];

const splitTitle = (title = "") => {
  const cleanTitle = String(title).replace(/<[^>]*>/g, "").trim();
  const words = cleanTitle.split(" ").filter(Boolean);

  if (words.length > 2) {
    return {
      title: words.slice(0, words.length - 2).join(" "),
      highlight: words.slice(-2).join(" "),
    };
  }

  return {
    title: cleanTitle || "Présentation",
    highlight: "",
  };
};

export default function About() {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const fetchSettings = async () => {
      try {
        const data = await getSettings();
        const clubSettings = Array.isArray(data) ? data[0] : data;

        if (!mounted) return;

        setSettings({
          ...DEFAULT_SETTINGS,
          ...(clubSettings || {}),
        });
      } catch (error) {
        console.error("Erreur lors de la récupération des paramètres :", error);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchSettings();

    return () => {
      mounted = false;
    };
  }, []);

  const { title, highlight } = splitTitle(settings?.about_title);
  const logoUrl = getImageUrl(settings?.logo_club);

  return (
    <section
      id="about"
      className="relative scroll-mt-28 overflow-hidden bg-slate-50 py-20 text-slate-900 dark:bg-slate-950 dark:text-white lg:py-28"
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.12),transparent_32%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.10),transparent_30%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(148,163,184,0.12)_1px,transparent_1px),linear-gradient(to_bottom,rgba(148,163,184,0.12)_1px,transparent_1px)] bg-[size:42px_42px] opacity-40 dark:opacity-20" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="mx-auto max-w-3xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-orange-700 dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-300">
            <FaCheckCircle />
            Présentation
          </div>

          <h2 className="mt-6 font-barlow text-4xl font-extrabold tracking-tight text-slate-950 dark:text-white sm:text-5xl">
            {title}{" "}
            {highlight && (
              <span className="text-primary">
                {highlight}
              </span>
            )}
          </h2>

          <p className="mx-auto mt-5 max-w-2xl text-base leading-8 text-slate-600 dark:text-slate-300">
            Découvrez l’identité du club, ses valeurs et les informations
            essentielles pour pratiquer dans les meilleures conditions.
          </p>
        </Reveal>

        <div className="mt-14 grid items-center gap-10 lg:grid-cols-[0.85fr_1.15fr]">
          <Reveal variant="left">
            <div className="relative mx-auto max-w-md lg:mx-0">
              <div className="absolute -left-8 -top-8 h-40 w-40 rounded-full bg-primary/20 blur-3xl" />
              <div className="absolute -bottom-8 -right-8 h-44 w-44 rounded-full bg-blue-500/10 blur-3xl" />

              <div className="relative overflow-hidden rounded-[2.5rem] border border-slate-200 bg-white p-6 shadow-2xl dark:border-white/10 dark:bg-slate-900">
                <div className="flex min-h-[320px] items-center justify-center rounded-[2rem] bg-gradient-to-br from-slate-50 to-slate-100 p-8 dark:from-slate-950 dark:to-slate-900">
                  {logoUrl ? (
                    <img
                      src={logoUrl}
                      alt={`Logo ${settings?.nom_club || "club"}`}
                      className="max-h-64 w-full object-contain drop-shadow-xl"
                    />
                  ) : (
                    <div className="flex h-44 w-44 items-center justify-center rounded-[2rem] bg-primary text-5xl font-bold text-white shadow-xl">
                      MC
                    </div>
                  )}
                </div>

                <div className="mt-5 rounded-[1.7rem] border border-slate-200 bg-slate-50 p-5 dark:border-white/10 dark:bg-white/5">
                  <p className="text-xs font-bold uppercase tracking-[0.22em] text-slate-400">
                    Club
                  </p>
                  <p className="mt-1 font-barlow text-2xl font-extrabold text-slate-950 dark:text-white">
                    {settings?.nom_club || "MonAppliClub Light"}
                  </p>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal variant="right" delay={120}>
            <div className="rounded-[2.5rem] border border-slate-200 bg-white/90 p-6 shadow-xl backdrop-blur-xl dark:border-white/10 dark:bg-slate-900/80 sm:p-8">
              {loading ? (
                <div className="space-y-4">
                  <div className="h-5 w-2/3 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
                  <div className="h-5 w-full animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
                  <div className="h-5 w-5/6 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
                  <div className="h-5 w-3/4 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
                </div>
              ) : (
                <div
                  className="prose prose-slate max-w-none text-base leading-8 text-slate-600 dark:prose-invert dark:text-slate-300 prose-p:my-4 prose-strong:text-slate-950 dark:prose-strong:text-white prose-a:font-bold prose-a:text-primary"
                  dangerouslySetInnerHTML={{
                    __html:
                      settings?.about_content ||
                      DEFAULT_SETTINGS.about_content,
                  }}
                />
              )}

              <div className="mt-8 grid gap-4 sm:grid-cols-3">
                {strengths.map((item, index) => {
                  const Icon = item.icon;

                  return (
                    <Reveal
                      key={item.title}
                      variant="zoom"
                      delay={index * 90}
                      className="rounded-3xl border border-slate-200 bg-slate-50 p-5 dark:border-white/10 dark:bg-white/5"
                    >
                      <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                        <Icon />
                      </div>

                      <h3 className="font-barlow text-lg font-bold text-slate-950 dark:text-white">
                        {item.title}
                      </h3>

                      <p className="mt-2 text-sm leading-6 text-slate-500 dark:text-slate-400">
                        {item.text}
                      </p>
                    </Reveal>
                  );
                })}
              </div>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <a
                  href="/club"
                  className="inline-flex items-center justify-center gap-3 rounded-full bg-slate-950 px-6 py-3 text-sm font-bold text-white shadow-lg transition hover:-translate-y-0.5 hover:bg-slate-800 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-100"
                >
                  Découvrir le club
                  <FaArrowRight />
                </a>

                <a
                  href="/inscription"
                  className="inline-flex items-center justify-center gap-3 rounded-full border border-slate-200 bg-white px-6 py-3 text-sm font-bold text-slate-800 shadow-sm transition hover:-translate-y-0.5 hover:border-orange-200 hover:bg-orange-50 hover:text-orange-700 dark:border-white/10 dark:bg-white/5 dark:text-white dark:hover:bg-white/10"
                >
                  Rejoindre le club
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}